/**
 * PURPOSE: Reglas puras de las firmas digitales dentro de la Vista Planilla FEDUP (FTR-0062): modos, contadores, geometría de las casillas
 *   sobre el visor nativo de PDF, desplazamiento a la zona de cada modo y clasificación de errores. Sin React ni red.
 * RESPONSIBILITIES:
 *  - Sí: decidir si el visor nativo permite el overlay de casillas (solo Chrome / Chromium: medido) o si se usa el panel de respaldo.
 *  - Sí: convertir la geometría de la hoja (puntos PDF que entrega el backend) a píxeles del iframe con el modelo medido del visor de Chrome.
 *  - Sí: mensajes claros por cada respuesta del backend (423 oficializado, 409 conflicto, 503 no habilitado, red…).
 *  - No: no pide nada al backend ni toca la planilla digital.
 * COLLABORATORS:
 *  - useFedupSignatureController.js, FedupSignatureOverlay.jsx, FedupSignaturePanel.jsx, FedupSignatureModeSelector.jsx, FedupSignaturePad.jsx
 * NOTES — modelo del visor de Chrome (medido con Puppeteer en Chrome for Testing 135 y Chrome 154, anchos de iframe de 380 a 3500 px,
 *   devicePixelRatio 1/1.25/1.5/2; claude-verification/ftr-0062/): con `#view=FitH` y el iframe tan alto como la hoja, el visor usa el zoom
 *   z = ancho / (anchoHojaEnPx + 10) con anchoHojaEnPx = puntos × 96/72, y deja 5·z px a la izquierda y 3·z px arriba. El ajuste fino
 *   (−0.3 px en x, 0 en y) sale de medir las casillas DENTRO de la Vista Planilla (diálogo centrado: el contenido del visor se ajusta al
 *   píxel del dispositivo); error medido ≤ 2 px. Edge NO respeta FitH con escalado de Windows > 100 % y Firefox/Safari no se midieron:
 *   ahí panel.
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/fedupSignatures.utils.js
 */

import { canEmbedPdf, isRequestCanceled } from "../fedupPreview.utils.js";

export const SIGNATURE_MODES = Object.freeze({ PRE: "pre", FINAL: "final" });

export const SIGNATURE_MODE_LIST = Object.freeze([
  Object.freeze({ mode: SIGNATURE_MODES.PRE, label: "Firmas previas", title: "Firmas previas: capitanes y entrenadores (recuadro FIRMAS)" }),
  Object.freeze({ mode: SIGNATURE_MODES.FINAL, label: "Firmas finales", title: "Firmas finales: árbitros, anotador y capitanes" }),
]);

// Partido oficializado (regla revisada de FTR-0062): solo las casillas CON firma activa quedan bloqueadas; las vacías se pueden firmar.
export const OFFICIAL_LOCK_MESSAGE = "Partido oficializado: esta firma ya no se puede modificar";
export const OFFICIAL_SIGN_NOTE = "Partido oficializado: una vez guardada, esta firma no se podrá modificar ni quitar";

/** Casilla bloqueada: partido oficializado y firma activa (el backend lo manda en `locked`; si falta, se deduce). */
export function isSlotLocked(slot, official) {
  if (!slot) return false;
  if (typeof slot.locked === "boolean") return slot.locked;
  return Boolean(official && slot.signature);
}

export const SIGNATURE_UI_MESSAGES = Object.freeze({
  network: "No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo.",
  conflict: "La casilla cambió en otra pestaña o equipo. Se actualizó el estado de las firmas.",
  unavailable: "Las firmas digitales aún no están habilitadas en este servidor.",
  auth: "Tu sesión expiró. Vuelve a iniciar sesión para firmar.",
  notFound: "La casilla ya no tiene esa firma. Se actualizó el estado de las firmas.",
  invalid: "La firma no es válida. Bórrala y vuelve a firmar.",
  server: "El servidor no pudo guardar la firma. Inténtalo de nuevo en unos segundos.",
  status: "No se pudo cargar el estado de las firmas.",
});

export function getModeInfo(mode) {
  return SIGNATURE_MODE_LIST.find((m) => m.mode === mode) || null;
}

export function slotsOfMode(status, mode) {
  return (status?.slots || []).filter((s) => s.mode === mode);
}

/** "2/4" (firmadas / total) de un modo; "–" sin estado. */
export function modeCounter(status, mode) {
  const info = (status?.modes || []).find((m) => m.mode === mode);
  return info ? `${info.signed}/${info.total}` : "–";
}

// ---------- visor nativo ----------

const OTHER_CHROMIUM_UA = /\b(Edg|EdgA|EdgiOS|OPR|Opera|YaBrowser|SamsungBrowser|Vivaldi|Brave)\//i;
const OTHER_CHROMIUM_BRAND = /Microsoft Edge|Opera|Brave|Yandex|Vivaldi|Samsung/i;

/**
 * "chrome" = visor PDF de Chrome/Chromium (geometría medida: overlay); "other" = otro visor (Edge, Firefox, Safari…: panel);
 * "none" = sin visor embebido (panel + "Abrir en pestaña nueva").
 */
export function detectPdfViewerEngine(nav = typeof navigator !== "undefined" ? navigator : null) {
  if (!nav || !canEmbedPdf(nav)) return "none";
  const ua = String(nav.userAgent || "");
  const brands = Array.isArray(nav.userAgentData?.brands) ? nav.userAgentData.brands.map((b) => String(b?.brand || "")) : [];
  if (brands.length) {
    if (brands.some((b) => OTHER_CHROMIUM_BRAND.test(b))) return "other";
    if (brands.some((b) => /^(Google Chrome|Chromium|HeadlessChrome)$/i.test(b))) return OTHER_CHROMIUM_UA.test(ua) ? "other" : "chrome";
    return "other";
  }
  if (/\b(Chrome|HeadlessChrome|Chromium)\/\d+/.test(ua) && !OTHER_CHROMIUM_UA.test(ua) && !/\bMobile\b/i.test(ua)) return "chrome";
  return "other";
}

const PX_PER_PT = 96 / 72;
const CHROME_DOC_EXTRA_PX = 10;
const CHROME_INSET_X = 5;
const CHROME_INSET_Y = 3;
const CHROME_FIX_X = -0.3;
const CHROME_FIX_Y = 0;

/** Dónde dibuja el visor de Chrome la hoja dentro de un iframe de `frameWidth` px: { scale (px por pt), offsetX, offsetY } en px CSS. */
export function chromePdfPageMap(frameWidth, pageWidthPt = 1190) {
  const w = Number(frameWidth);
  const pageW = Number(pageWidthPt);
  if (!(w > 0) || !(pageW > 0)) return null;
  const z = w / (pageW * PX_PER_PT + CHROME_DOC_EXTRA_PX);
  return { scale: z * PX_PER_PT, offsetX: CHROME_INSET_X * z + CHROME_FIX_X, offsetY: CHROME_INSET_Y * z + CHROME_FIX_Y };
}

/** Rectángulo de una casilla (pt, origen arriba-izquierda) en px CSS dentro del iframe. */
export function boxToFrameRect(box, map) {
  if (!box || !map) return null;
  return {
    left: map.offsetX + box.x0 * map.scale,
    top: map.offsetY + box.y0 * map.scale,
    width: (box.x1 - box.x0) * map.scale,
    height: (box.y1 - box.y0) * map.scale,
  };
}

export function unionRect(rects) {
  const list = (rects || []).filter(Boolean);
  if (!list.length) return null;
  const left = Math.min(...list.map((r) => r.left));
  const top = Math.min(...list.map((r) => r.top));
  const right = Math.max(...list.map((r) => r.left + r.width));
  const bottom = Math.max(...list.map((r) => r.top + r.height));
  return { left, top, width: right - left, height: bottom - top };
}

/**
 * Scroll del contenedor para centrar `rect` (px dentro del iframe): el iframe está a (frameLeft, frameTop) dentro del contenido del
 * contenedor. Acotado a [0, máx].
 */
export function scrollTargetFor(rect, { frameLeft = 0, frameTop = 0, clientWidth, clientHeight, scrollWidth, scrollHeight }) {
  if (!rect) return null;
  const clamp = (v, max) => Math.max(0, Math.min(Math.max(0, max), Math.round(v)));
  return {
    left: clamp(frameLeft + rect.left + rect.width / 2 - clientWidth / 2, scrollWidth - clientWidth),
    top: clamp(frameTop + rect.top + rect.height / 2 - clientHeight / 2, scrollHeight - clientHeight),
  };
}

/** Misma regla que el backend (renderVolleyballFedupSheetPdf.signatureImageRect): la firma centrada y sin deformar dentro de `inkBox`. */
export function signatureImageRect(inkBox, imgW, imgH, margin = 0.6) {
  const availW = Math.max(0, inkBox.x1 - inkBox.x0 - 2 * margin);
  const availH = Math.max(0, inkBox.y1 - inkBox.y0 - 2 * margin);
  const scale = imgW > 0 && imgH > 0 ? Math.min(availW / imgW, availH / imgH) : 0;
  const w = imgW * scale;
  const h = imgH * scale;
  const x0 = inkBox.x0 + margin + (availW - w) / 2;
  const y0 = inkBox.y0 + margin + (availH - h) / 2;
  return { x0, y0, x1: x0 + w, y1: y0 + h };
}

// ---------- errores ----------

/**
 * Clasifica un fallo de lectura o escritura de firmas.
 * @returns {{ kind: "canceled"|"official"|"conflict"|"unavailable"|"auth"|"notFound"|"invalid"|"network"|"server", status: number, message: string }}
 */
export function classifySignatureError(err) {
  if (isRequestCanceled(err)) return { kind: "canceled", status: 0, message: "" };
  const status = Number(err?.response?.status ?? err?.status) || 0;
  const body = err?.response?.data || {};
  const code = String(body?.code || err?.code || "");
  const serverMessage = typeof body?.msg === "string" ? body.msg.trim().slice(0, 200) : "";
  if (!status) return { kind: "network", status, message: SIGNATURE_UI_MESSAGES.network };
  if (status === 423 || code === "MATCH_OFFICIALIZED") return { kind: "official", status, message: OFFICIAL_LOCK_MESSAGE };
  if (status === 409 && code === "SIGNATURE_CONFLICT") return { kind: "conflict", status, message: SIGNATURE_UI_MESSAGES.conflict };
  if (status === 503 && code === "SIGNATURES_NOT_AVAILABLE") return { kind: "unavailable", status, message: SIGNATURE_UI_MESSAGES.unavailable };
  if (status === 401) return { kind: "auth", status, message: SIGNATURE_UI_MESSAGES.auth };
  if (status === 404 && code === "SIGNATURE_NOT_FOUND") return { kind: "notFound", status, message: SIGNATURE_UI_MESSAGES.notFound };
  if (status === 400 || status === 413 || (status === 409 && code === "SHEET_NOT_AVAILABLE")) {
    return { kind: "invalid", status, message: serverMessage || SIGNATURE_UI_MESSAGES.invalid };
  }
  return { kind: "server", status, message: SIGNATURE_UI_MESSAGES.server };
}

// ---------- identificador del equipo (solo en memoria) ----------

let deviceId = null;

/** Identificador aleatorio de esta pestaña, solo en memoria (no se guarda en localStorage ni en cookies). */
export function getSignatureDeviceId() {
  if (!deviceId) {
    let random = "";
    try {
      const bytes = new Uint8Array(6);
      window.crypto.getRandomValues(bytes);
      random = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    } catch {
      random = Math.random().toString(16).slice(2, 14);
    }
    deviceId = `winscore-web:${random}`;
  }
  return deviceId;
}
